import React from "react"
import {
  Box,
  Text,
  Icon,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  Flex,
  DrawerBody,
  PseudoBox,
  Button as ChakraButton,
  useDisclosure,
} from "@chakra-ui/core"
import Body from "../../components/typography/Body"
import SmallCaps from "../../components/typography/SmallCaps"
import { Button } from "../../components/Buttons"
import { Filter, isAtLeastOneFilterApplied } from "./utils"

type MobileFiltersProps = {
  filters: Filter[]
  setFilters: (filters: Filter[]) => void
}

export const MobileTopLevelFilter: React.FC<{
  onClick?: VoidFunction
  isDisabled?: boolean
}> = ({ children, isDisabled, onClick }) => (
  <PseudoBox
    w="100%"
    p="0.875rem 1.375rem"
    justifyContent="space-between"
    alignItems="center"
    borderBottom="1px solid"
    borderColor="dividerLine"
    display="flex"
    _active={{ bg: "dividerLine" }}
    onClick={!isDisabled ? onClick : undefined}
    opacity={!isDisabled ? 1 : 0.5}
    // @ts-ignore
    disabled={isDisabled}
  >
    <Text
      fontFamily="avenirNext"
      fontSize="0.9375rem"
      lineHeight="2.25rem"
      color="dusk"
      textTransform="capitalize"
    >
      {children}
    </Text>
    <Icon
      name="chevron"
      size="1.25rem"
      transform="rotate(180deg)"
      color="dusk"
    />
  </PseudoBox>
)

export const MobileFilterSelect: React.FC<{
  selected: boolean
  onClick?: VoidFunction
}> = ({ children, selected, onClick }) => (
  <PseudoBox
    onClick={onClick}
    _active={{ bg: "dividerLine" }}
    borderBottom="1px solid"
    borderBottomColor="dividerLine"
    p="1.25rem"
  >
    <Flex justifyContent="space-between" alignItems="center">
      <Body lineHeight="1.375rem" color={selected ? "sunrise" : "dusk"}>
        {children}
      </Body>
      <Icon
        name="checkMark"
        size="1rem"
        color="sunrise"
        opacity={selected ? 1 : 0}
      />
    </Flex>
  </PseudoBox>
)

export default function MobileFilters({
  filters,
  setFilters,
  ...props
}: MobileFiltersProps) {
  const [unappliedFilters, setUnappliedFilters] = React.useState<
    Array<{
      title: string
      options: Array<{ title: string; name: string; selected: boolean }>
    }>
  >(filters)

  // Filter Drawer
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [mobileFilterOpenTitle, setMobileFilterOpenTitle] =
    React.useState<string>("")

  const resetMobileFilterOpenTitle = () => setMobileFilterOpenTitle("")

  const getAllSelectedFilters = () =>
    filters.reduce(
      (acc, cur) => [...acc, ...cur.options.filter((opt) => opt.selected)],
      []
    )

  const clearFiltersMappingFunction = (data: {
    title: string
    options: Array<{ title: string; name: string; selected: boolean }>
  }) => ({
    ...data,
    options: data.options.map((opt) => ({ ...opt, selected: false })),
  })
  const clearFilters = () =>
    setFilters(filters.map(clearFiltersMappingFunction))

  const clearUnappliedFilters = () =>
    setUnappliedFilters(unappliedFilters.map(clearFiltersMappingFunction))

  const toggleUnappliedSelected = (title: string, name: string) =>
    setUnappliedFilters(
      unappliedFilters.map(({ title: t, options }) => ({
        title: t,
        options:
          title === t
            ? options.map((opt) => ({
                ...opt,
                selected: opt.name === name ? !opt.selected : opt.selected,
              }))
            : options,
      }))
    )
  const atLeastOneFilterApplied = isAtLeastOneFilterApplied(filters)

  return (
    <>
      <ChakraButton
        onClick={() => {
          onOpen()
          setUnappliedFilters(filters)
        }}
        border={atLeastOneFilterApplied ? "none" : "1px solid"}
        borderColor="noon"
        w="100%"
        h="3rem"
        mb="1rem"
        display="flex"
        alignItems="center"
        justifyContent="cneter"
        borderRadius="0.5rem"
        bg={atLeastOneFilterApplied ? "sunrise" : "none"}
        _active={{ bg: "dividerLine" }}
      >
        <Icon
          name={atLeastOneFilterApplied ? "fullFilter" : "filter"}
          size="0.75rem"
          color={atLeastOneFilterApplied ? "white" : "dusk"}
        />
        {atLeastOneFilterApplied && (
          <Body
            size="sm"
            fontWeight="500"
            lineHeight="1.0625rem"
            isTruncated
            color="white"
            flexGrow={1}
          >
            {getAllSelectedFilters()?.map(
              ({ title }, index, array) =>
                `${title}${index + 1 === array.length ? "" : ","}`
            )}
          </Body>
        )}
        {!atLeastOneFilterApplied && (
          <SmallCaps size="large" fontWeight="500" color="dusk" flexGrow={1}>
            Filter
          </SmallCaps>
        )}
        <Icon
          name="pencil"
          size="1rem"
          color="white"
          visibility={atLeastOneFilterApplied ? "visible" : "hidden"}
        />
      </ChakraButton>
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent borderRadius="1.5rem 1.5rem 0 0">
          <DrawerHeader borderBottom="1px solid" borderColor="dividerLine">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              m="1.5rem 0"
            >
              <Icon name="x" size="2rem" visibility="hidden" />
              <SmallCaps
                size="lg"
                fontWeight="bold"
                lineHeight="1.0625rem"
                color="dusk"
              >
                Filter
              </SmallCaps>
              <Icon
                name="x"
                size="2rem"
                onClick={() => {
                  onClose()
                  resetMobileFilterOpenTitle()
                  setUnappliedFilters(filters)
                }}
                color="dusk"
              />
            </Flex>
          </DrawerHeader>
          <DrawerBody p="0">
            <Box h="60vh">
              <Box overflow="auto" h="calc(60vh - 6.25rem)">
                {filters?.map((f) => (
                  <MobileTopLevelFilter
                    key={f.title}
                    isDisabled={f.options.length === 0}
                    onClick={() => setMobileFilterOpenTitle(f.title)}
                  >
                    {f.title == "wheelSize" ? "Wheel size" : f.title}
                  </MobileTopLevelFilter>
                ))}
              </Box>
              <Box
                pos="absolute"
                top="7.05rem"
                left="0"
                h="calc(60vh - 6.25rem)"
                w="100%"
                bg="white"
                overflowY="auto"
                opacity={mobileFilterOpenTitle === "" ? 0 : 1}
                zIndex={mobileFilterOpenTitle === "" ? -1 : 100}
                transition="opacity 500ms"
              >
                {unappliedFilters?.map(({ title, options }) => (
                  <Box
                    key={`${title}-options`}
                    display={
                      title === mobileFilterOpenTitle ? "contents" : "none"
                    }
                  >
                    <PseudoBox
                      bg="noon"
                      onClick={() => resetMobileFilterOpenTitle()}
                      _active={{ bg: "dividerLine" }}
                    >
                      <Flex p="1.25rem" alignItems="center">
                        <Icon
                          name="chevron"
                          size="1.25rem"
                          color="dusk"
                          stroke="dusk"
                        />
                        <Body
                          textTransform="capitalize"
                          lineHeight="1.375rem"
                          color="dusk"
                          ml="0.75rem"
                        >
                          {title == "wheelSize" ? "Wheel size" : title}
                        </Body>
                      </Flex>
                    </PseudoBox>
                    {options
                      //remove duplicate
                      .filter(
                        (v, i, a) =>
                          a.findIndex((v2) => v2.name === v.name) === i
                      )
                      .filter((opt) => {
                        return opt.name
                      })
                      .map((opt) => (
                        <MobileFilterSelect
                          key={opt.name}
                          selected={opt.selected}
                          onClick={() =>
                            toggleUnappliedSelected(title, opt.name)
                          }
                        >
                          {opt.title}
                        </MobileFilterSelect>
                      ))}
                  </Box>
                ))}
              </Box>
              <Flex
                pos="absolute"
                bottom="0"
                left="0"
                width="100%"
                p="1.875rem 1.25rem"
                boxShadow="0 1.25rem 6.25rem white"
              >
                {atLeastOneFilterApplied && (
                  <Button
                    theme="secondary"
                    onClick={() => {
                      clearFilters()
                      clearUnappliedFilters()
                      onClose()
                    }}
                    mr="1.25rem"
                  >
                    <SmallCaps
                      size="lg"
                      fontWeight="medium"
                      lineHeight="1.0625rem"
                      color="dusk"
                    >
                      Clear All
                    </SmallCaps>
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setFilters(unappliedFilters)
                    onClose()
                    resetMobileFilterOpenTitle()
                  }}
                  flexGrow={1}
                >
                  <SmallCaps size="lg" fontWeight="bold" lineHeight="1.0625rem">
                    Apply
                  </SmallCaps>
                </Button>
              </Flex>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
